import React from 'react';
import { LayoutSize, useLayoutSizeValue } from '@/contexts/layout-context';
import { PageLayout, PageLayoutProps, YStack } from '@usepepper/sprout-kit-ui';
import { useShellChrome } from '@experiences/templates/shell-chrome-context';
import { useTheme } from '@/contexts/theme-context';

/** Gap between body sections. */
const BODY_CONTENT_GAP = '$16';

/** Horizontal gutter at the widest layout. */
const LARGE_LAYOUT_GUTTER = 100;

type BodySpace = NonNullable<PageLayoutProps['bodyGutter']>;

type AppPageLayoutProps = Omit<PageLayoutProps, 'headerGutter'>;

/**
 * Responsive page gutter. Sits outside the focused max-width, so it shows at
 * narrow widths and is absorbed by the centering margin once the body caps out.
 */
const usePageGutter = (): BodySpace => {
  const theme = useTheme();
  return useLayoutSizeValue({
    [LayoutSize.SMALL]: theme.sk.spacing.spaceLarge,
    [LayoutSize.SMALL_TABLET]: theme.sk.spacing.spaceLarge,
    [LayoutSize.MEDIUM]: theme.sk.spacing.space3xlarge,
    [LayoutSize.LARGE]: LARGE_LAYOUT_GUTTER,
  }) as BodySpace;
};

/** App page layout with responsive gutter plus the shell's leading actions. */
export const AppPageLayout = ({
  bodyGutter,
  children,
  leadingActions,
  mode,
  ...rest
}: AppPageLayoutProps) => {
  const responsiveGutter = usePageGutter();
  // The top bar uses a flat gutter. The body gutter is responsive on focused
  // layouts (absorbed by the centering margin) and full-bleed on fluid.
  const isFocused = mode === 'focused';
  const headerGutter: BodySpace = '$24';
  const resolvedBodyGutter =
    bodyGutter ?? (isFocused ? responsiveGutter : '$0');

  const { leadingAction } = useShellChrome();
  const mergedLeadingActions =
    leadingAction || leadingActions ? (
      <>
        {leadingAction}
        {leadingActions}
      </>
    ) : undefined;

  return (
    <PageLayout
      bodyGutter={resolvedBodyGutter}
      headerGutter={headerGutter}
      leadingActions={mergedLeadingActions}
      mode={mode}
      {...rest}
    >
      <YStack flex={1} gap={BODY_CONTENT_GAP}>
        <>{children}</>
      </YStack>
    </PageLayout>
  );
};
